import * as React from "react";
import { AnalyticsStore } from "bernie-core";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkScrim } from "uitk-react-scrim";
import { UitkRating } from "uitk-react-rating";
import { UitkExpandoLink, UitkExpandoLinkTypeSize } from "uitk-react-expando";
import { UitkPrimaryButton } from "uitk-react-button";
import { AnalyticsConfig } from "uitk-react-utils";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import {
  expandoCollapsedTitle,
  expandoExpandedTitle,
} from "components/flexComponents/Hotels/views/HotelsViews/HotelUtils";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { withStores } from "stores";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";

interface HotelImageCardProps {
  context: ExtendedContextStore;
  model: HotelsFlexModuleResult;
  hotel: Hotel;
  analytics: AnalyticsStore;
}

const HotelCard = withStores("analytics")((props: HotelImageCardProps) => {
  const { context, model, hotel } = props;
  const { callToAction, contentRecipe, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const { formatText } = useLocalization();

  // covered in e2e test
  /* istanbul ignore next */
  const expandoLinkAnalytics: AnalyticsConfig = {
    id: "LP.Hotels.HotelImageTop.ExpandoLink",
    callback: (theId, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: theId,
        action: Action.CLICK,
        linkName: description,
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const ButtonTracked = FlexClickTracker(UitkPrimaryButton);

  return (
    <UitkCard>
      <UitkCardContentSection padded={false}>
        <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
          <LazyLoadedImage src={hotelImageUrl} alt={hotelName} context={context} />
          <UitkScrim>
            <UitkHeading tag="h3" size={5} className="uitk-type-500">
              {hotelName}
            </UitkHeading>
            <UitkRating
              rating={hotelStarRating}
              icon={ratingIcon(context.locale)}
              overlay
              className={context.site.brand}
            />
            <UitkText size={300}>{hotelFormattedFullAddress}</UitkText>
          </UitkScrim>
        </UitkFigure>

        <UitkCardContentSection>
          <UitkLayoutFlex justifyContent="space-between">
            <UitkLayoutFlex direction="column">
              <ValueProposition hotel={hotel} />
              <DistanceOrRating model={model} hotel={hotel} className="all-t-margin-three" context={context} />
            </UitkLayoutFlex>
            <UitkLayoutFlex className="uitk-type-right">
              <PriceLockup hotel={hotel} priceA11y={new HotelPriceLockup(hotel, formatText)} hasGetRatesText />
            </UitkLayoutFlex>
          </UitkLayoutFlex>
        </UitkCardContentSection>

        <UitkCardLink>
          <TrackedLink
            data-testid={`hotelcard-link-${hotelName}`}
            aria-label={hotelName}
            moduleName="hotels"
            rfrr="card"
            rel={getHotelLinkRel(callToAction)}
            href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
          />
        </UitkCardLink>
      </UitkCardContentSection>

      <UitkCardContentSection border="top">
        <UitkExpandoLink
          type={UitkExpandoLinkTypeSize.TYPE_200}
          isVisible={false}
          expandoTitle={expandoCollapsedTitle(contentRecipe)}
          expandedLabel={expandoExpandedTitle(contentRecipe)}
          analytics={expandoLinkAnalytics}
        >
          <DescriptionOrReview model={model} hotel={hotel} className="all-t-margin-three" />
          <AmenitiesOrRecommendation model={model} hotel={hotel} className="all-t-margin-three" />
          <ButtonTracked
            href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
            isFullWidth
            className="all-t-margin-three"
            moduleName="LP.hotels.hotelImageTop"
            rfrr="expandoExpanded.select"
          >
            {formatText("hotelsViews.expando.select")}
          </ButtonTracked>
        </UitkExpandoLink>
      </UitkCardContentSection>
    </UitkCard>
  );
});

export default HotelCard;
